/**
 * ChargeCatalogPicker
 *
 * Source picker for the Add-charge modals. Three tabs sourced from active menus
 * + services: Bar & Restaurant · Merchandising · Services. Token badges show
 * only in the staff context (`forStaff`), since only staff pay with tokens.
 * Shared by the guest and staff charge dialogs.
 *
 * Two selection modes:
 *  - single (default, guest charge): one item + a quantity.
 *  - multiple (`multiple`, staff charge): a cart of lines, each with its own
 *    quantity, plus a running total.
 *
 * The item list has NO inner scroll of its own: the tabs + search are pinned to
 * the top and the list flows in the dialog's single scroll region. A second,
 * nested scroll here trapped the wheel — you had to bottom-out the list before
 * the modal would keep scrolling down to the reason/authorization fields.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@/core';
import { useLocationCurrency } from '@/shared/hooks/useLocationCurrency';
import { Input } from '@/theme/components/ui/input';
import { cn } from '@/lib/utils';
import type { ChargeCatalog, ChargeCatalogItem } from '@/modules/menus/services/chargeCatalogService';

type TabKey = 'barRestaurant' | 'merchandising' | 'services';

/** A picked item with its own quantity (multi-select mode). */
export interface ChargeLine {
  item: ChargeCatalogItem;
  qty: number;
}

const itemKey = (i: ChargeCatalogItem) => `${i.sourceType}:${i.id}`;

interface ChargeCatalogPickerProps {
  catalog: ChargeCatalog | undefined;
  isLoading: boolean;
  forStaff: boolean;
  /** Single-select mode (guest charge). */
  selected?: ChargeCatalogItem | null;
  onSelect?: (item: ChargeCatalogItem) => void;
  quantity?: string;
  onQuantityChange?: (q: string) => void;
  /** Multi-select mode (staff charge): a cart of lines. */
  multiple?: boolean;
  lines?: ChargeLine[];
  onToggle?: (item: ChargeCatalogItem) => void;
  onLineQtyChange?: (item: ChargeCatalogItem, qty: number) => void;
  /**
   * When true (default) the item list scrolls inside its own bounded box — for
   * a host dialog that does NOT scroll its body. Set false when the picker sits
   * in a dialog whose body already scrolls (the staff charge modal): the list
   * then flows and the body is the single scroll region, so the wheel is never
   * trapped inside the list.
   */
  listScroll?: boolean;
}

export function ChargeCatalogPicker({
  catalog,
  isLoading,
  forStaff,
  selected,
  onSelect,
  quantity,
  onQuantityChange,
  multiple = false,
  lines = [],
  onToggle,
  onLineQtyChange,
  listScroll = true,
}: ChargeCatalogPickerProps) {
  const { t } = useTranslation();
  const { formatPrice } = useLocationCurrency();
  const [tab, setTab] = useState<TabKey>('barRestaurant');
  const [query, setQuery] = useState('');

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'barRestaurant', label: t('addCharge.tabs.barRestaurant', 'Bar & Restaurant') },
    { key: 'merchandising', label: t('addCharge.tabs.merchandising', 'Merchandising') },
    { key: 'services', label: t('addCharge.tabs.services', 'Services') },
  ];

  const items = useMemo(() => {
    const list = catalog?.[tab] ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q ? list.filter((i) => i.name.toLowerCase().includes(q)) : list;
    // group by section, preserving order
    const groups: { section: string; items: ChargeCatalogItem[] }[] = [];
    for (const it of filtered) {
      const section = it.section || t('addCharge.otherSection', 'Other');
      let g = groups.find((x) => x.section === section);
      if (!g) { g = { section, items: [] }; groups.push(g); }
      g.items.push(it);
    }
    return groups;
  }, [catalog, tab, query, t]);

  const selectedKeys = useMemo(
    () => new Set(lines.map((l) => itemKey(l.item))),
    [lines],
  );
  const isPicked = (it: ChargeCatalogItem) =>
    multiple ? selectedKeys.has(itemKey(it)) : selected?.sourceType === it.sourceType && selected?.id === it.id;

  const qty = parseFloat(quantity ?? '1');
  const cartTotal = lines.reduce((sum, l) => sum + (parseFloat(l.item.price) || 0) * l.qty, 0);

  return (
    <div className="space-y-3">
      {/* Source tabs + search. When the whole picker scrolls in the dialog body
          (listScroll=false) they pin to the top so they stay reachable. */}
      <div className={cn('space-y-2', !listScroll && 'sticky top-0 z-10 bg-background pb-1')}>
        <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={cn(
                'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                tab === tb.key ? 'bg-background text-foreground shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <Input
          placeholder={t('addCharge.search', 'Search the menu…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9"
        />
      </div>

      <div className={cn('rounded-lg border border-border', listScroll && 'max-h-[260px] overflow-y-auto')}>
        {isLoading ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
        ) : items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('addCharge.empty', 'No items in the active menus for this source.')}
          </p>
        ) : (
          items.map((group) => (
            <div key={group.section}>
              <div className={cn(
                'bg-muted/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                listScroll && 'sticky top-0',
              )}>
                {group.section}
              </div>
              {group.items.map((it) => {
                const isSel = isPicked(it);
                return (
                  <button
                    key={itemKey(it)}
                    type="button"
                    onClick={() => (multiple ? onToggle?.(it) : onSelect?.(it))}
                    className={cn(
                      'flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left first:border-t-0 hover:bg-muted/50',
                      isSel && 'bg-primary/10',
                    )}
                  >
                    <span className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] text-primary-foreground',
                      multiple ? 'rounded-[4px]' : 'rounded',
                      isSel ? 'border-primary bg-primary' : 'border-border',
                    )}>
                      {multiple && isSel ? '✓' : null}
                    </span>
                    <span className="flex-1 text-sm text-foreground">{it.name}</span>
                    {forStaff && it.tokenEligible && (
                      <span className="rounded border border-success/30 bg-success/10 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold text-success">
                        {t('addCharge.tokens', '{{n}} tok', { n: it.tokenCost })}
                      </span>
                    )}
                    <span className="min-w-[52px] text-right font-mono text-[13px] font-semibold tabular-nums">
                      {formatPrice(parseFloat(it.price) || 0)}
                    </span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {multiple ? (
        /* Cart — selected lines, each with its own quantity, plus a total. */
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
          {lines.length === 0 ? (
            <p className="py-1 text-center text-xs text-muted-foreground">
              {t('addCharge.cartEmpty', 'No items selected yet. Tap items above to add them.')}
            </p>
          ) : (
            <div className="space-y-1.5">
              {lines.map((l) => (
                <div key={itemKey(l.item)} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-[12.5px] text-foreground">{l.item.name}</span>
                  <div className="flex items-center overflow-hidden rounded-md border border-border bg-background">
                    <button type="button" className="h-6 w-6 text-muted-foreground" onClick={() => onLineQtyChange?.(l.item, Math.max(1, l.qty - 1))}>−</button>
                    <span className="w-7 border-x border-border text-center font-mono text-[12px] font-semibold">{l.qty}</span>
                    <button type="button" className="h-6 w-6 text-muted-foreground" onClick={() => onLineQtyChange?.(l.item, l.qty + 1)}>+</button>
                  </div>
                  <span className="min-w-[56px] text-right font-mono text-[12.5px] font-semibold tabular-nums">
                    {formatPrice((parseFloat(l.item.price) || 0) * l.qty)}
                  </span>
                  <button
                    type="button"
                    aria-label={t('common.remove', 'Remove')}
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => onToggle?.(l.item)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border/60 pt-1.5">
                <span className="text-xs font-semibold text-muted-foreground">
                  {t('addCharge.cartTotal', 'Total · {{count}} item(s)', { count: lines.length })}
                </span>
                <span className="font-mono text-[15px] font-bold tabular-nums">{formatPrice(cartTotal)}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Single-select quantity + selected line. */
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <span className="text-xs font-semibold text-muted-foreground">{t('addCharge.qty', 'Qty')}</span>
          <div className="flex items-center overflow-hidden rounded-md border border-border bg-background">
            <button type="button" className="h-7 w-7 text-muted-foreground" onClick={() => onQuantityChange?.(String(Math.max(1, (qty || 1) - 1)))}>−</button>
            <span className="w-9 border-x border-border text-center font-mono text-[13px] font-semibold">{quantity}</span>
            <button type="button" className="h-7 w-7 text-muted-foreground" onClick={() => onQuantityChange?.(String((qty || 0) + 1))}>+</button>
          </div>
          {selected && (
            <div className="ml-auto text-right">
              <div className="text-xs text-muted-foreground">{selected.name}</div>
              <div className="font-mono text-[15px] font-bold tabular-nums">
                {formatPrice((parseFloat(selected.price) || 0) * (qty || 0))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
